/**
 * Multi-select sector filter with type-in search. Distinct from the shared
 * MultiSelectFilter (which is dark-on-dark and lacks a search input) — this
 * one is light-themed for the IntelPage filter bar and lets analysts narrow
 * a long sector list quickly.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Sector } from '../../types';
import styles from './SectorMultiSelect.module.css';

interface Props {
  sectors: Sector[];
  selected: Set<number>;
  onChange: (next: Set<number>) => void;
}

export function SectorMultiSelect({ sectors, selected, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Click-outside / Esc to close.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    // Focus the search input when opening.
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sectors;
    return sectors.filter((s) => s.name.toLowerCase().includes(q));
  }, [sectors, search]);

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const clear = () => onChange(new Set());

  const selectVisible = () => {
    const next = new Set(selected);
    for (const s of filtered) next.add(s.id);
    onChange(next);
  };

  const triggerLabel =
    selected.size === 0
      ? 'Sectors: All'
      : selected.size === 1
        ? `Sectors: 1 selected`
        : `Sectors: ${selected.size} selected`;

  return (
    <div ref={ref} className={styles.root}>
      <button
        type="button"
        className={`${styles.trigger} ${selected.size > 0 ? styles.triggerActive : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {triggerLabel}
        <span className={styles.chevron}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchRow}>
            <input
              ref={inputRef}
              type="search"
              className={styles.searchInput}
              placeholder="Filter sectors…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className={styles.actionsRow}>
            {search.trim() && filtered.length > 0 && (
              <button
                type="button"
                className={styles.actionBtn}
                onClick={selectVisible}
              >
                Select {filtered.length} visible
              </button>
            )}
            {selected.size > 0 && (
              <button
                type="button"
                className={styles.actionBtn}
                onClick={clear}
              >
                Clear ({selected.size})
              </button>
            )}
          </div>
          <div className={styles.options}>
            {filtered.length === 0 ? (
              <div className={styles.noMatch}>No sectors match "{search}"</div>
            ) : (
              filtered.map((s) => (
                <label key={s.id} className={styles.option}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={selected.has(s.id)}
                    onChange={() => toggle(s.id)}
                  />
                  <span className={styles.optionLabel}>{s.name}</span>
                  <span className={styles.optionStage}>{s.stage}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
